import * as React from 'react';
import { Form, Tooltip } from 'antd';
import { Rule, FormItemProps } from 'antd/lib/form';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { TypeUtils } from '../../../common/misc';
import { isEmpty } from 'lodash';
import './CustomFormItem.less';

type CustomProps = {
    children: React.ReactElement;
    required?: boolean;
    isReadOnly?: boolean;
    message?: string;
    messageStyle?: React.CSSProperties;
    customValidator?: (rule: unknown, value: unknown, setError: (error: string) => void) => Promise<void>;
};

type Props = CustomProps & Omit<FormItemProps, 'rules'>;

const CustomFormItem: React.FC<Props> = props => {
    const {
        children,
        required,
        isReadOnly,
        message = 'Please fill out this field',
        messageStyle,
        customValidator,
        ...formItemProps
    } = props;

    const [error, setError] = React.useState<string>('');

    const [tooltipVisible, setTooltipVisible] = React.useState<boolean>(false);

    const [isHovered, setIsHovered] = React.useState<boolean>(false);

    const valueIsValid = React.useCallback((value: unknown) => {
        if (TypeUtils.isNumber(value)) {
            return !isNaN(value) && isFinite(value);
        }

        if (value instanceof Date) {
            return !isNaN(value.getTime());
        }

        if (TypeUtils.isDateTime(value)) {
            return value.isValid;
        }

        if (TypeUtils.isString(value)) {
            return value.trim().length > 0;
        }

        return !isEmpty(value);
    }, []);

    const defaultValidator = React.useCallback(
        (_: unknown, value: unknown) => {
            if (!valueIsValid(value)) {
                return Promise.reject(new Error(message));
            }

            return Promise.resolve();
        },
        [message, valueIsValid]
    );

    const formItemValidator = React.useCallback(
        async (rule: unknown, value: unknown) => {
            if (isReadOnly) {
                setError('');
                return Promise.resolve();
            }

            try {
                if (required) {
                    await defaultValidator(rule, value);
                }

                if (customValidator) {
                    let customError = '';

                    await customValidator(rule, value, (errorMessage: string) => {
                        customError = errorMessage;
                    }).catch((validationError: unknown) => {
                        const normalizedError =
                            customError ||
                            (validationError instanceof Error && validationError.message ? validationError.message : message);

                        throw new Error(normalizedError);
                    });
                }

                setError('');
                return Promise.resolve();
            } catch (validationError) {
                const normalizedError =
                    validationError instanceof Error && validationError.message ? validationError.message : message;

                setError(normalizedError);
                return Promise.reject(new Error(normalizedError));
            }
        },
        [customValidator, defaultValidator, isReadOnly, message, required]
    );

    const formItemRules = React.useMemo(() => {
        if (isReadOnly || (!required && !customValidator)) {
            return [];
        }

        const rules: Rule[] = [{ validator: formItemValidator }];

        return rules;
    }, [required, isReadOnly, customValidator, formItemValidator]);

    const getTitle = () => {
        if (!error) {
            return null;
        }

        return (
            <div className="custom-form-item-message-container">
                <ExclamationCircleOutlined className="custom-form-item-message-icon" />
                <span>{error}</span>
            </div>
        );
    };

    React.useEffect(() => {
        if (!formItemRules.length) {
            setError('');
        }
    }, [formItemRules]);

    React.useEffect(() => {
        if (isHovered && error) {
            setTooltipVisible(true);
        } else if (!isHovered) {
            setTooltipVisible(false);
        } else if (!error) {
            setTooltipVisible(false);
        }
    }, [error, isHovered]);

    const handleMouseEnter = () => setIsHovered(true);

    const handleMouseLeave = () => setIsHovered(false);

    const clonedChildren = React.cloneElement(children, {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        hasError: !!error,
        required: isReadOnly ? false : required
    });

    return (
        <Tooltip
            visible={tooltipVisible}
            placement="bottom"
            overlayClassName="custom-form-item-message"
            overlayInnerStyle={messageStyle}
            getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
            title={getTitle()}
            overlayStyle={{ maxWidth: 400 }}
        >
            <Form.Item className="custom-form-item" rules={formItemRules} {...formItemProps}>
                {clonedChildren}
            </Form.Item>
        </Tooltip>
    );
};

export default CustomFormItem;
